// components/recipes/workflow/WorkflowPanel.tsx
//
// Store-backed container: maps recipeStore <-> WorkflowSentenceView via the pure
// mapping module (mapping.ts), which the parity harness exercises against real
// prod recipe shapes. This is the drop-in replacement for the legacy
// "Action + Frequency" sidebar.
import React from 'react'
import { observer } from 'mobx-react-lite'
import { recipeStore } from '../../../stores/recipe_store'
import { WorkflowSentenceView } from './WorkflowSentenceView'
import { buildWorkflowModel, makeWorkflowHandlers, numericVariableOptions } from './mapping'

const currencySymbol = (): string => (window as any)?.marketplace?.currency_symbol ?? '$'

// The parent hydrates timezone data only for private V2 segment recipes. Keep
// that loaded capability when MobX re-renders this child before its boolean
// prop has caught up with the response-derived recipe version.
export const resolveClockSchedulingAvailable = (
    clockSchedulingAvailable: boolean,
    scheduleTimezones: unknown,
): boolean => clockSchedulingAvailable || scheduleTimezones !== null

export const WorkflowPanel = observer(
    ({ clockSchedulingAvailable }: { clockSchedulingAvailable: boolean }) => {
        const occurrences = recipeStore.scheduleOccurrences ?? []
        const effectiveClockSchedulingAvailable = resolveClockSchedulingAvailable(
            clockSchedulingAvailable,
            recipeStore.scheduleTimezones,
        )
        const model = buildWorkflowModel(recipeStore.recipe, {
            actionOptions: recipeStore.availableActions.map((a) => ({
                value: a.value,
                label: a.label,
            })),
            variableOptions: numericVariableOptions(recipeStore.variables),
            version: recipeStore.version,
            currency: currencySymbol(),
            clockSchedulingAvailable: effectiveClockSchedulingAvailable,
            scheduleEditor: effectiveClockSchedulingAvailable
                ? {
                      draft: recipeStore.scheduleDraft,
                      timezones: recipeStore.scheduleTimezones,
                      preview: occurrences.length
                          ? {
                                occurrences,
                                max_runs_per_day: recipeStore.scheduleMaxRunsPerDay ?? 0,
                                has_nearby_schedule: recipeStore.scheduleHasNearby,
                            }
                          : null,
                      previewError: recipeStore.scheduleError,
                      persistedSchedule: recipeStore.originalSchedule !== null,
                      nearbySchedule: recipeStore.scheduleHasNearby,
                      adType: recipeStore.recipe.ad_type,
                      onDraftChange: (draft) => recipeStore.setScheduleDraft(draft),
                      onPreview: async (draft) => {
                          await recipeStore.previewSchedule(draft)
                      },
                      onValidityChange: (valid) => recipeStore.setScheduleEditorValid(valid),
                  }
                : undefined,
        })

        return (
            <WorkflowSentenceView
                model={model}
                handlers={makeWorkflowHandlers(recipeStore, {
                    clockSchedulingAvailable: effectiveClockSchedulingAvailable,
                })}
            />
        )
    },
)
